import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Loader from 'legacy/common/components/layout/Loader';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { showToast } from 'legacy/common/utils/toast';
import {
  useAdmitGuestMutation,
  useGetEventEntryDetail
} from 'legacy/events/hooks';
import { GuestEntryListItem } from 'legacy/events/types';
import { Trans, useTranslation } from 'react-i18next';
import { BiConversation } from 'react-icons/bi';
import { BsArrowLeft } from 'react-icons/bs';
import { FaCheck } from 'react-icons/fa6';
import { IoMdBarcode } from 'react-icons/io';
import { MdChecklistRtl } from 'react-icons/md';
import i18n from 'i18n';

interface EntryDetailProps {
  eventId: string;
  entry: GuestEntryListItem;
  onClose: () => void;
  onClickScan: () => void;
}

const EntryDetail: React.FC<EntryDetailProps> = ({
  eventId,
  entry,
  onClose,
  onClickScan
}) => {
  const { t } = useTranslation(['events']);

  const { data, isLoading } = useGetEventEntryDetail({
    eventId,
    ticketId: entry.id
  });

  const admitGuestMutation = useAdmitGuestMutation();

  const onScan = () => {
    onClose();
    onClickScan();
  };

  const onPressAdmit = () => {
    admitGuestMutation.mutate(
      {
        eventId,
        ticketId: entry.id
      },
      {
        onSuccess: () => {
          showToast({
            type: 'success',
            message: (
              <Trans
                i18nKey="events:manageEntry.admitSuccess"
                components={{
                  strong: <strong />
                }}
                values={{
                  name:
                    entry?.type === 'complimentary'
                      ? entry?.name
                      : `${entry?.user?.firstName} ${entry?.user?.lastName}`
                }}
              />
            )
          });
        }
      }
    );
  };

  return (
    <Modal
      visible
      type={ModalType.SMALL}
      renderFooter={() => (
        <div className="px-4 flex flex-row w-full items-center justify-center">
          <div className="flex-1 mr-4">
            <Button
              loading={admitGuestMutation.isLoading}
              disabled={isLoading}
              onClick={onPressAdmit}
              icon={<FaCheck className="text-primary-500" />}
            >
              {t('events:manageEntry.admit')}
            </Button>
          </div>
          <div className="flex-1">
            <Button
              type={ButtonType.Primary}
              onClick={onScan}
              icon={<IoMdBarcode />}
            >
              {t('events:manageEntry.scan')}
            </Button>
          </div>
        </div>
      )}
    >
      {isLoading ? (
        <div className="p-20">
          <Loader isLoading />
        </div>
      ) : (
        <div className="relative h-full overflow-auto">
          <div className="absolute top-4 left-2 h-7 w-7 bg-gray-100 z-30 rounded-full flex items-center justify-center cursor-pointer">
            <div onClick={onClose}>
              <BsArrowLeft />
            </div>
          </div>

          <div className="absolute top-0 left-0 h-24 w-full bg-linear-to-r from-[#D0CEFF] to-[#C4DDFF] z-10" />

          <div className="relative pt-11 mx-auto flex flex-col items-center justify-center z-20">
            <div className="w-24 h-24 object-cover rounded-full border-2 border-gray-100 overflow-hidden align-center justify-center flex items-center bg-white">
              <img
                className="w-full h-full"
                src={data?.user?.photoUrl || '/img/search/user.svg'}
              />
            </div>

            <div className="text-2xl mt-4 font-semibold">
              {data?.type === 'complimentary'
                ? data?.name
                : `${data?.user?.firstName} ${data?.user?.lastName}`}
            </div>
          </div>

          <div className="p-8">
            <div className="border-b border-gray-100" />
            <div className="text-lg pt-4 pb-2 text-center">🎟️</div>
            {data?.bundleProductName && (
              <div className="text-lg text-center">
                <span className="font-semibold">
                  {t('events:tickets.bundle', { postProcess: 'colon' })}{' '}
                </span>
                {data?.bundleProductName}
              </div>
            )}
            <div className="text-lg pb-4 text-center">
              <span className="font-semibold">
                {t('events:tickets.ticket', { postProcess: 'colon' })}{' '}
              </span>
              {data?.tier?.name}
            </div>
            {data?.type === 'complimentary' ? (
              <div className="pb-6">
                <div className="text-sm text-center">
                  {t('events:manageEntry.createdBy')}{' '}
                  <div className="font-semibold">
                    {data?.createdBy?.firstName} {data?.createdBy?.lastName}
                  </div>
                </div>
                <div className="mt-1 text-sm text-center">
                  {t('events:manageEntry.createdOn')}{' '}
                  <div className="font-semibold">
                    {new Date(data?.createdAt)
                      .toLocaleDateString(i18n.language, {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })
                      .replace(/[.-]/g, '/')}
                  </div>
                </div>
              </div>
            ) : (
              <div className="pb-6">
                <div className="text-sm text-center">
                  {t('events:manageEntry.purchasedBy')}{' '}
                  <div className="font-semibold">
                    {data?.purchaser?.firstName} {data?.purchaser?.lastName}
                  </div>
                </div>
                <div className="mt-1 text-sm text-center">
                  {t('events:manageEntry.purchasedOn')}{' '}
                  <div className="font-semibold">
                    {new Date(data?.purchasedAt).toLocaleString(i18n.language, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </div>
                </div>
              </div>
            )}
            <div className="border-b border-gray-100" />
          </div>

          {data?.entryLog && data?.entryLog?.length > 0 && (
            <div className="px-8 mb-8">
              <div className="mb-4 text-sm items-center flex flex-row">
                <div className="mr-1">
                  <IoMdBarcode />
                </div>
                <div className="font-bold">
                  {t('events:manageEntry.entryLog')}
                </div>
              </div>

              {data?.entryLog?.map((entry) => (
                <div className="pb-4" key={entry.date.toString()}>
                  <div className="text-sm font-semibold">
                    {t(`events:manageEntry.${entry?.type}`)}{' '}
                    <div className="text-sm font-normal">
                      {t('events:manageEntry.entryBy', {
                        date: new Date(entry.date).toLocaleString(
                          i18n.language,
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true
                          }
                        ),
                        name: `${entry?.user?.firstName} ${entry?.user?.lastName}`
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {data?.screeningQuestions && data?.screeningQuestions?.length > 0 && (
            <div className="px-8 mb-8">
              <div className="mb-4 text-sm items-center flex flex-row">
                <div className="mr-1">
                  <MdChecklistRtl />
                </div>
                <div className="font-bold">
                  {t('events:scanQR.screeningQuestions')}
                </div>
              </div>
              {data?.screeningQuestions?.map((answer) => (
                <div className="pb-8" key={answer.questionId}>
                  <div className="text-sm font-semibold">
                    {answer?.question}
                  </div>
                  <div className="text-sm font-normal">{answer?.answer}</div>
                </div>
              ))}
            </div>
          )}

          {data?.guestQuestions && data?.guestQuestions?.length > 0 && (
            <div className="px-8">
              <div className="mb-4 text-sm items-center flex flex-row">
                <div className="mr-1">
                  <BiConversation />
                </div>
                <div className="font-bold">
                  {t('events:scanQR.guestQuestions')}
                </div>
              </div>
              {data?.guestQuestions?.map((answer) => (
                <div className="pb-8" key={answer.questionId}>
                  <div className="text-sm font-semibold">
                    {answer?.question}
                  </div>
                  <div className="text-sm font-normal">{answer?.answer}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default EntryDetail;
